import { atomFamily } from 'jotai/utils';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';
import { useAtom } from 'jotai';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ChevronDown, ChevronUp, X } from '@mynaui/icons-react';

import { cn } from '@/lib/utils';

/**
 * Renders the volume integration metrics computed by `scripts/deviation.py`
 * (Approach 1, raster integration) on the derived POINTCLOUD layer's
 * `metadata.deviationStats.volume`.
 *
 * Three display states:
 *   - **collapsed** (default): one-liner with net volume + tolerance %.
 *     Tiny footprint so it doesn't crowd the colorbar.
 *   - **expanded**: full KPI grid + worst-case footer. Surfaces-mode adds
 *     a per-surface table.
 *   - **dismissed**: panel hidden until the user re-shows it (per-layer
 *     setting persisted in sessionStorage).
 *
 * The collapsed/expanded/dismissed state is per-layer-id and stored in
 * sessionStorage so it sticks across re-renders without polluting
 * persistent storage.
 */

interface SurfaceStats {
  featureId?: number;
  featureName?: string;
  pointCount?: number;
  positiveVolumeM3?: number;
  negativeVolumeM3?: number;
  netVolumeM3?: number;
  coverageAreaM2?: number;
  withinTolerancePct?: number;
}

interface VolumeStats {
  positiveVolumeM3?: number;
  negativeVolumeM3?: number;
  netVolumeM3?: number;
  coverageAreaM2?: number;
  withinTolerancePct?: number;
  withinToleranceMM?: number;
  worstPositiveDeviationM?: number;
  worstPositiveX?: number;
  worstPositiveY?: number;
  worstPositiveZ?: number;
  worstNegativeDeviationM?: number;
  worstNegativeX?: number;
  worstNegativeY?: number;
  worstNegativeZ?: number;
  rasterPlane?: 'xy' | 'fitted' | string;
  rasterResolutionM?: number;
  referenceMode?: 'tin' | 'surfaces' | string;
  surfaces?: SurfaceStats[];
}

type PanelState = 'collapsed' | 'expanded' | 'dismissed';

const panelStateStorage = createJSONStorage<PanelState>(() => sessionStorage);

/**
 * Per-layer panel state, scoped to the current browser tab. Defaults to
 * `collapsed` so the panel never explodes onto the screen unsolicited;
 * the user opts into the full KPI grid when they want detail.
 */
const volumePanelStateAtom = atomFamily((layerId: string) =>
  atomWithStorage<PanelState>(`@actegon.gis.deviation.volume.${layerId}`, 'collapsed', panelStateStorage),
);

interface Props {
  layerId: string;
  layerName: string;
  volume: VolumeStats;
}

const formatVolume = (m3: number | undefined): string => {
  if (m3 == null || !Number.isFinite(m3)) return '—';
  const abs = Math.abs(m3);
  const decimals = abs >= 1 ? 3 : 4;
  const sign = m3 > 0 ? '+' : m3 < 0 ? '−' : '';
  return `${sign}${abs.toFixed(decimals)} m³`;
};

const formatArea = (m2: number | undefined): string => {
  if (m2 == null || !Number.isFinite(m2)) return '—';
  return `${m2.toFixed(2)} m²`;
};

const formatPct = (pct: number | undefined): string => {
  if (pct == null || !Number.isFinite(pct)) return '—';
  return `${pct.toFixed(1)} %`;
};

const formatDeviation = (m: number | undefined): string => {
  if (m == null || !Number.isFinite(m)) return '—';
  const abs = Math.abs(m);
  const sign = m > 0 ? '+' : m < 0 ? '−' : '';
  if (abs < 0.005) return `${sign}${(abs * 1000).toFixed(1)} mm`;
  if (abs < 0.5) return `${sign}${(abs * 100).toFixed(2)} cm`;
  return `${sign}${abs.toFixed(3)} m`;
};

const formatCoord = (n: number | undefined): string =>
  n != null && Number.isFinite(n) ? n.toFixed(2) : '—';

export function DeviationVolumePanel({ layerId, layerName, volume }: Props) {
  const { t } = useTranslation('project');
  const [state, setState] = useAtom(volumePanelStateAtom(layerId));
  // Local UI for the per-surface sub-section: collapsed by default even
  // when the parent is expanded, since the table can grow long for
  // multi-wall projects.
  const [surfacesOpen, setSurfacesOpen] = useState(false);

  if (!volume) return null;

  const toleranceMm = volume.withinToleranceMM ?? 10;
  const isExpanded = state === 'expanded';
  const surfaces = volume.surfaces ?? [];

  // Dismissed → render a tiny "Show volume" pill instead of nothing, so the
  // user can always bring the panel back. Without this affordance dismiss
  // would be effectively permanent for the session and make the feature
  // hidden until a hard reload.
  if (state === 'dismissed') {
    return (
      <button
        type='button'
        onClick={() => setState('collapsed')}
        className='pointer-events-auto self-end rounded-md border border-slate-200/70 bg-white/80 px-2 py-0.5 text-[11px] text-slate-600 shadow-sm backdrop-blur-md hover:bg-white hover:text-slate-900 dark:border-stone-700/70 dark:bg-stone-900/80 dark:text-stone-300 dark:hover:bg-stone-900 dark:hover:text-white'
        title={t('deviationVolume.show')}
      >
        {t('deviationVolume.show')}
      </button>
    );
  }

  return (
    <div className='pointer-events-auto rounded-md border border-slate-200/70 bg-white/90 px-3 py-1.5 text-xs shadow-sm backdrop-blur-md dark:border-stone-700/70 dark:bg-stone-900/90'>
      {/* Header: title + layer name + collapse/dismiss controls. Always
          visible; the rest is conditional on expansion. */}
      <div className='flex items-center justify-between gap-2'>
        <button
          type='button'
          onClick={() => setState(isExpanded ? 'collapsed' : 'expanded')}
          className='flex flex-1 items-center gap-1.5 text-left'
          title={isExpanded ? t('deviationVolume.collapse') : t('deviationVolume.expand')}
        >
          {isExpanded ? <ChevronUp className='size-3.5' /> : <ChevronDown className='size-3.5' />}
          <span className='font-medium text-slate-900 dark:text-white'>
            {t('deviationVolume.title')}
          </span>
          {!isExpanded && (
            <span className='ml-2 font-mono tabular-nums text-slate-700 dark:text-stone-200'>
              {formatVolume(volume.netVolumeM3)}
            </span>
          )}
          {!isExpanded && volume.withinTolerancePct != null && (
            <span className='ml-2 text-slate-500 dark:text-stone-400'>
              · {formatPct(volume.withinTolerancePct)}
            </span>
          )}
        </button>
        <div className='flex items-center gap-1'>
          <span className='max-w-[140px] truncate text-slate-500 dark:text-stone-400' title={layerName}>
            {layerName}
          </span>
          <button
            type='button'
            onClick={() => setState('dismissed')}
            title={t('deviationVolume.dismiss')}
            className='size-5 rounded text-slate-500 hover:bg-slate-200/70 hover:text-slate-900 dark:text-stone-400 dark:hover:bg-stone-700/70 dark:hover:text-white'
          >
            <X className='size-3.5' />
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className='mt-2'>
          <dl className='grid grid-cols-[max-content_1fr] gap-x-3 gap-y-0.5'>
            <dt className='text-slate-600 dark:text-stone-300'>{t('deviationVolume.positive')}</dt>
            <dd className='text-right font-mono tabular-nums text-emerald-600 dark:text-emerald-400'>
              {formatVolume(volume.positiveVolumeM3)}
            </dd>

            <dt className='text-slate-600 dark:text-stone-300'>{t('deviationVolume.negative')}</dt>
            <dd className='text-right font-mono tabular-nums text-rose-600 dark:text-rose-400'>
              {formatVolume(volume.negativeVolumeM3)}
            </dd>

            <dt className='text-slate-600 dark:text-stone-300'>{t('deviationVolume.net')}</dt>
            <dd className='text-right font-mono tabular-nums text-slate-900 dark:text-white'>
              {formatVolume(volume.netVolumeM3)}
            </dd>

            <dt className='text-slate-600 dark:text-stone-300'>{t('deviationVolume.coverage')}</dt>
            <dd className='text-right font-mono tabular-nums text-slate-900 dark:text-white'>
              {formatArea(volume.coverageAreaM2)}
            </dd>

            <dt className='text-slate-600 dark:text-stone-300'>
              {t('deviationVolume.withinTolerance', { tolerance: toleranceMm.toFixed(0) })}
            </dt>
            <dd className='text-right font-mono tabular-nums text-slate-900 dark:text-white'>
              {formatPct(volume.withinTolerancePct)}
            </dd>
          </dl>

          {(volume.worstPositiveDeviationM != null || volume.worstNegativeDeviationM != null) && (
            <div className='mt-2 border-t border-slate-200/70 pt-1.5 text-[11px] text-slate-500 dark:border-stone-700/70 dark:text-stone-400'>
              {volume.worstPositiveDeviationM != null && (
                <div className='flex items-center justify-between gap-3'>
                  <span>{t('deviationVolume.worstPositive')}</span>
                  <span className='font-mono tabular-nums'>
                    {formatDeviation(volume.worstPositiveDeviationM)}
                    <span className='ml-1 opacity-70'>
                      ({formatCoord(volume.worstPositiveX)}, {formatCoord(volume.worstPositiveY)},{' '}
                      {formatCoord(volume.worstPositiveZ)})
                    </span>
                  </span>
                </div>
              )}
              {volume.worstNegativeDeviationM != null && (
                <div className='flex items-center justify-between gap-3'>
                  <span>{t('deviationVolume.worstNegative')}</span>
                  <span className='font-mono tabular-nums'>
                    {formatDeviation(volume.worstNegativeDeviationM)}
                    <span className='ml-1 opacity-70'>
                      ({formatCoord(volume.worstNegativeX)}, {formatCoord(volume.worstNegativeY)},{' '}
                      {formatCoord(volume.worstNegativeZ)})
                    </span>
                  </span>
                </div>
              )}
              {(volume.rasterPlane != null || volume.rasterResolutionM != null) && (
                <div className='mt-1 flex items-center justify-between gap-3 opacity-70'>
                  {volume.rasterPlane != null && (
                    <span>
                      {t('deviationVolume.plane')}: {volume.rasterPlane}
                    </span>
                  )}
                  {volume.rasterResolutionM != null && (
                    <span>
                      {t('deviationVolume.resolution')}: {(volume.rasterResolutionM * 100).toFixed(1)} cm
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Per-surface breakdown — only for `surfaces` mode. Collapsed by
              default to keep the panel compact; users with 1–3 surfaces
              will expand often, users with 20+ won't. The full table is
              also downloadable as a CSV byproduct (registered server-side
              with the `volumes-…csv` filename). */}
          {surfaces.length > 0 && (
            <div className='mt-2 border-t border-slate-200/70 pt-1.5 dark:border-stone-700/70'>
              <button
                type='button'
                onClick={() => setSurfacesOpen((v) => !v)}
                className='flex w-full items-center gap-1 text-left text-slate-700 dark:text-stone-200'
              >
                {surfacesOpen ? <ChevronUp className='size-3' /> : <ChevronDown className='size-3' />}
                <span>{t('deviationVolume.perSurface', { count: surfaces.length })}</span>
              </button>
              {surfacesOpen && (
                <div className='mt-1.5 max-h-56 overflow-auto'>
                  <table className='w-full text-[11px]'>
                    <thead className='text-slate-600 dark:text-stone-300'>
                      <tr>
                        <th className='py-0.5 pr-2 text-left font-medium'>
                          {t('deviationVolume.tableSurface')}
                        </th>
                        <th className='py-0.5 pr-2 text-right font-medium text-emerald-700 dark:text-emerald-400'>
                          +
                        </th>
                        <th className='py-0.5 pr-2 text-right font-medium text-rose-700 dark:text-rose-400'>
                          −
                        </th>
                        <th className='py-0.5 pr-2 text-right font-medium'>
                          {t('deviationVolume.tableNet')}
                        </th>
                        <th className='py-0.5 pr-2 text-right font-medium'>
                          {t('deviationVolume.tableTol')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {surfaces.map((s, i) => (
                        <tr
                          key={s.featureId ?? i}
                          className={cn(
                            'border-t border-slate-100 dark:border-stone-800',
                            i % 2 === 1 && 'bg-slate-50/40 dark:bg-stone-900/40',
                          )}
                        >
                          <td className='py-0.5 pr-2 truncate' title={s.featureName ?? ''}>
                            {s.featureName ?? `#${s.featureId ?? i}`}
                          </td>
                          <td className='py-0.5 pr-2 text-right font-mono tabular-nums text-emerald-600 dark:text-emerald-400'>
                            {formatVolume(s.positiveVolumeM3)}
                          </td>
                          <td className='py-0.5 pr-2 text-right font-mono tabular-nums text-rose-600 dark:text-rose-400'>
                            {formatVolume(s.negativeVolumeM3)}
                          </td>
                          <td className='py-0.5 pr-2 text-right font-mono tabular-nums'>
                            {formatVolume(s.netVolumeM3)}
                          </td>
                          <td className='py-0.5 pr-2 text-right font-mono tabular-nums'>
                            {formatPct(s.withinTolerancePct)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
